<template>
  <!--
    Header door switcher: "Viewing as: Developer" opens a menu of the five
    doors plus "Just browsing". Choosing updates localStorage["cs.persona"];
    on "/" or a "/for/*" page it also navigates to the matching door (or "/"
    for browsing). Elsewhere it only updates state.

    `variant="drawer"` renders the same choices as a flat list for the mobile
    navigation drawer, where a popover would fight the drawer's own focus trap.
  -->
  <div v-if="variant === 'drawer'" class="pw pw--drawer">
    <p class="pw-drawer-title">
      <i class="fas fa-door-open" aria-hidden="true"/>
      <span>{{ $t('persona.switcher.viewingAs') }}</span>
    </p>
    <div class="pw-drawer-list" role="group" :aria-label="$t('persona.switcher.menuLabel')">
      <button
        v-for="opt in options"
        :key="String(opt.id)"
        type="button"
        class="pw-drawer-item"
        :class="{ 'is-active': isActive(opt.id) }"
        :aria-pressed="isActive(opt.id) ? 'true' : 'false'"
        @click="pick(opt.id)"
      >
        <span>{{ $t(opt.shortKey) }}</span>
        <i v-if="isActive(opt.id)" class="fas fa-check" aria-hidden="true"/>
      </button>
    </div>
  </div>

  <div v-else
ref="root"
class="pw"
@keydown.esc.stop.prevent="close(true)">
    <button
      ref="trigger"
      type="button"
      class="pw-trigger"
      :aria-expanded="open ? 'true' : 'false'"
      aria-haspopup="menu"
      :aria-label="`${$t('persona.switcher.viewingAs')}: ${currentShort}. ${$t('persona.switcher.menuLabel')}`"
      @click="toggle"
    >
      <span class="pw-trigger-label">{{ $t('persona.switcher.viewingAs') }}</span>
      <span class="pw-trigger-value">{{ currentShort }}</span>
      <i class="fas fa-chevron-down pw-chevron" :class="{ 'is-open': open }" aria-hidden="true"/>
    </button>

    <transition name="pw-pop">
      <div
        v-if="open"
        ref="menu"
        class="pw-menu"
        role="menu"
        :aria-label="$t('persona.switcher.menuLabel')"
        @keydown="onMenuKeydown"
      >
        <button
          v-for="opt in options"
          :key="String(opt.id)"
          type="button"
          role="menuitemradio"
          class="pw-item"
          :class="{ 'is-active': isActive(opt.id), 'is-ghost': opt.id === null }"
          :aria-checked="isActive(opt.id) ? 'true' : 'false'"
          @click="pick(opt.id)"
        >
          <span class="pw-item-text">
            <span class="pw-item-short">{{ $t(opt.shortKey) }}</span>
            <span class="pw-item-label">{{ $t(opt.labelKey) }}</span>
          </span>
          <i v-if="isActive(opt.id)" class="fas fa-check" aria-hidden="true"/>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { DOORS, BROWSING, usePersona, getDoor } from '@/utils/persona'

export default {
  name: 'PersonaSwitcher',
  props: {
    variant: { type: String, default: 'desktop' }
  },
  emits: ['picked'],
  setup() {
    const { persona, setPersona } = usePersona()
    return { persona, setPersona }
  },
  data() {
    return { open: false, options: [...DOORS, BROWSING] }
  },
  computed: {
    currentId() {
      return this.persona ? this.persona.id : undefined
    },
    currentShort() {
      if (this.currentId === undefined) return this.$t('persona.switcher.choose')
      const door = getDoor(this.currentId)
      return this.$t(door ? door.shortKey : BROWSING.shortKey)
    }
  },
  beforeUnmount() {
    this.unbindOutside()
  },
  methods: {
    isActive(id) {
      if (this.currentId === undefined) return false
      return this.currentId === id
    },
    toggle() {
      if (this.open) this.close(true)
      else this.openMenu()
    },
    openMenu() {
      this.open = true
      document.addEventListener('click', this.onOutside, true)
      this.$nextTick(() => {
        const items = this.$refs.menu ? this.$refs.menu.querySelectorAll('[role="menuitemradio"]') : []
        const active = [...items].find((el) => el.getAttribute('aria-checked') === 'true') || items[0]
        if (active) active.focus()
      })
    },
    close(refocus) {
      if (!this.open) return
      this.open = false
      this.unbindOutside()
      if (refocus && this.$refs.trigger) this.$refs.trigger.focus()
    },
    unbindOutside() {
      document.removeEventListener('click', this.onOutside, true)
    },
    onOutside(event) {
      const root = this.$refs.root
      if (root && !root.contains(event.target)) this.close(false)
    },
    onMenuKeydown(event) {
      const items = [...this.$refs.menu.querySelectorAll('[role="menuitemradio"]')]
      const idx = items.indexOf(document.activeElement)
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        items[(idx + 1) % items.length].focus()
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        items[(idx - 1 + items.length) % items.length].focus()
      } else if (event.key === 'Home') {
        event.preventDefault()
        items[0].focus()
      } else if (event.key === 'End') {
        event.preventDefault()
        items[items.length - 1].focus()
      } else if (event.key === 'Tab') {
        this.close(false)
      }
    },
    pick(id) {
      this.setPersona(id)
      this.close(true)
      this.$emit('picked', id)
      const path = this.$route ? this.$route.path : ''
      const onDoorSurface = path === '/' || path.startsWith('/for/')
      if (!onDoorSurface) return
      const target = id ? `/for/${id}` : '/'
      if (target !== path) this.$router.push(target)
    }
  }
}
</script>

<style scoped>
.pw { position: relative; display: inline-flex; }

.pw-trigger {
  appearance: none;
  background: var(--ds-neutral-50);
  border: 1px solid var(--ds-neutral-200);
  border-radius: 999px;
  color: var(--ds-neutral-700);
  font: inherit;
  font-size: 0.82rem;
  padding: 0.4rem 0.7rem 0.4rem 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  white-space: nowrap;
  max-width: 100%;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.pw-trigger:hover { border-color: var(--ds-neutral-300); background: var(--ds-white); }
.pw-trigger:focus-visible { outline: 2px solid var(--ds-primary-500); outline-offset: 2px; }

.pw-trigger-label { color: var(--ds-neutral-500); }
.pw-trigger-label::after { content: ':'; }

.pw-trigger-value {
  font-weight: 600;
  color: var(--ds-primary-900);
  overflow: hidden;
  text-overflow: ellipsis;
}

.pw-chevron { font-size: 0.7rem; color: var(--ds-neutral-500); transition: transform 0.15s ease; }
.pw-chevron.is-open { transform: rotate(180deg); }

.pw-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 300px;
  max-width: min(360px, calc(100vw - 32px));
  background: var(--ds-white);
  border: 1px solid var(--ds-neutral-200);
  border-radius: var(--ds-radius-lg);
  box-shadow: var(--ds-shadow-lg);
  padding: 6px;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pw-item {
  appearance: none;
  background: transparent;
  border: 0;
  border-left: 3px solid transparent;
  border-radius: var(--ds-radius-sm);
  width: 100%;
  text-align: left;
  font: inherit;
  padding: 0.55rem 0.7rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  color: var(--ds-primary-900);
  cursor: pointer;
}

.pw-item:hover { background: var(--ds-neutral-50); }
.pw-item:focus-visible { outline: 2px solid var(--ds-primary-500); outline-offset: -2px; }
.pw-item.is-active { border-left-color: var(--ds-primary-500); background: var(--ds-primary-100); }
.pw-item.is-active i { color: var(--ds-primary-500); }
.pw-item.is-ghost .pw-item-short { color: var(--ds-neutral-700); }

.pw-item-text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.pw-item-short { font-weight: 600; font-size: 0.9rem; }
.pw-item-label { font-size: 0.8rem; color: var(--ds-neutral-500); }

.pw-pop-enter-active, .pw-pop-leave-active { transition: opacity 0.14s ease, transform 0.14s ease; }
.pw-pop-enter-from, .pw-pop-leave-to { opacity: 0; transform: translateY(-4px); }

/* Drawer variant */
.pw--drawer { display: block; }
.pw-drawer-title {
  margin: 0 0 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: var(--ds-neutral-500);
  font-size: 0.85rem;
  font-weight: 500;
}
.pw-drawer-title i { color: var(--ds-primary-500); }
.pw-drawer-list { display: flex; flex-wrap: wrap; gap: 6px; }
.pw-drawer-item {
  appearance: none;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 500;
  background: var(--ds-neutral-50);
  border: 1px solid var(--ds-neutral-200);
  color: var(--ds-neutral-700);
  border-radius: 999px;
  padding: 0.4rem 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
}
.pw-drawer-item.is-active {
  background: var(--ds-primary-100);
  border-color: var(--ds-primary-500);
  color: var(--ds-primary-900);
}
.pw-drawer-item.is-active i { color: var(--ds-primary-500); font-size: 0.75rem; }
.pw-drawer-item:focus-visible { outline: 2px solid var(--ds-primary-500); outline-offset: 2px; }

@media (prefers-reduced-motion: reduce) {
  .pw-trigger, .pw-chevron, .pw-pop-enter-active, .pw-pop-leave-active { transition: none; }
}
</style>
